import { FC, useState } from 'react';
import { Box, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import CustomButton from '../../common/CustomButton';
import CustomInput from '../../common/CustomInput';

import BinIcon from '../../assets/icons/documents/Trash_bin.png';
import PenIcon from '../../assets/icons/documents/Pen, Edit.png';
import CheckedIcon from '../../assets/icons/documents/checked.png';

import {
  useAddTagMutation,
  useDeleteTagMutation,
  useUpdateTagMutation
} from '../../store/services/tagsApi';
import { useAppDispatch } from '../../hooks/redux';
import { setTags, Tag } from '../../store/slices/tagsSlice';

import styles from './Tags.module.scss';

type TagsProps = {
  openManageFilters: boolean;
  tags: Tag[] | null;
  toggleManageDrawer: (arg: boolean) => void;
}

const Tags: FC<TagsProps> = ({
  openManageFilters = false,
  tags,
  toggleManageDrawer,
}) => {
  const dispatch = useAppDispatch();
  const [addTagMutation] = useAddTagMutation();
  const [deleteTagMutation] = useDeleteTagMutation();
  const [updateTagMutation] = useUpdateTagMutation();

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [tag, setTag] = useState<Tag | null>(null);
  const [tagName, setTagName] = useState<string>('');
  const [selectedTagColor, setSelectedTagColor] = useState<string | null>(null);
  const [openTagCreating, setOpenTagCreating] = useState<boolean>(false);

  const [openModal, setOpenModal] = useState<boolean>(false);


  const handleCreateTag = () => {
    setOpenTagCreating(true);
    setIsEditing(false);
    setTagName('');
    setSelectedTagColor(null);
  }

  const handleDeleteTag = async () => {
    if (!tag?.id) return;
    try {
      await deleteTagMutation({ id: tag?.id });
      dispatch(setTags(tags?.filter(t => t.id !== tag?.id) || []));
      setOpenModal(false);
      setTag(null);
    } catch (error) {
      console.error(error);
    }
  }

  const handleEditTag = (tag: Tag) => {
    setTagName(tag.name);
    setOpenTagCreating(true);
    setSelectedTagColor(tag.color);
    setIsEditing(true);
    setSelectedTagId(tag.id);
  }

  const handleOpenModal = (tag: Tag) => {
    setOpenModal(true);
    setTag(tag);
  }

  const handleAddTag = async () => {

    if (isEditing) {
      if (!selectedTagId) return;
      try {
        await updateTagMutation({ id: selectedTagId, name: tagName, color: selectedTagColor });
        dispatch(setTags(tags?.map(tag => tag.id === selectedTagId ? { ...tag, name: tagName, color: selectedTagColor } : tag) || []));
      } catch (error) {
        console.error('Error updating tag:', error);
      }
    } else {
      try {
        const resAddedTag = await addTagMutation({ name: tagName, color: selectedTagColor });
        dispatch(setTags([...(tags || []), resAddedTag.data]));
        setTagName('');
      } catch (error) {
        console.error('Error adding tag:', error);
      }
    }
  }

  return (
    <>
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: 'fit-content',
          minWidth: 343,
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: "20px",
          p: '32px 32px',
          overflowY: 'auto',
          boxShadow: 'none',
          maxHeight: '90vh',
          '@media (max-width: 900px)': {
            p: '32px 15px',
          },
        }}>
          <Box sx={{ position: 'absolute', top: 16, right: 20, cursor: 'pointer' }}>
            <CloseIcon sx={{ width: '30px', height: '30px' }} onClick={() => setOpenModal(false)} />
          </Box>
          <Box className={styles.modalContentWrapper}>
            <Typography
              variant='h5Bold'
              className={styles.title}
            >
              Are you sure?
            </Typography>
            <Typography
              variant='h10'
              className={styles.title}
            >
              {`You want to remove the tag “${tag?.name}”?`}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px', width: '100%', gap: '10px' }}>
              <CustomButton
                variant='outlined'
                onClick={() => setOpenModal(false)}
                customClass={styles.cancelBtn}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant='contained'
                onClick={handleDeleteTag}
                customClass={styles.editBtnDelete}
              >
                Delete tag
              </CustomButton>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Box className={`${styles.customDrawer} ${openManageFilters && styles.isShownManageTags}`}>
        <Box className={styles.customDrawerHeader}>
          <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '20px' }}>
            <Typography variant='h5Bold'>Tags management</Typography>
            <Typography variant='h10' sx={{ color: '#5E6A81' }}>You can have up to 5 tags</Typography>
          </Box>
          <CloseIcon className={styles.closeIcon} onClick={() => toggleManageDrawer(false)} />
        </Box>
        <Box className={styles.customDrawerContent}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '40px' }}>
            <Typography variant="h9Bold">Active Tags</Typography>
            <CustomButton
              variant='outlined'
              disabled={(tags?.length ?? 0) >= 5}
              onClick={handleCreateTag}
            >
              + Create new
            </CustomButton>
          </Box>

          {tags && tags.map(tag => (
            <Box
              key={tag.id}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                margin: '20px 0',
              }}
            >
              <Typography
                variant='h12Semibold'
                sx={{
                  bgcolor: tag.color,
                  padding: '5px 10px',
                  borderRadius: '16px',
                  height: '100%'
                }}
              >
                {tag.name}
              </Typography>

              <Box>
                <img
                  src={BinIcon}
                  alt='bin icon'
                  style={{ marginRight: '10px' }}
                  onClick={() => handleOpenModal(tag)}
                  className={styles.binIcon}
                />
                <img
                  src={PenIcon}
                  alt='pen icon'
                  onClick={() => handleEditTag(tag)}
                  className={styles.penIcon}
                />
              </Box>
            </Box>
          ))}

          {openTagCreating && <Box sx={{ marginTop: '20px' }}>
            <CustomInput inputLabel="" value={tagName} onChange={(e) => setTagName(e.target.value)} />
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', margin: '15px 0' }}>
              {["#FFF1A7", "#ECE2FF", "#FED7D4", "#D4F0FE", "#D4FEDA"].map((color) => {
                const isColorUsed = tags?.some(tag => tag.color === color && (!isEditing || tag.id !== selectedTagId));

                return (
                  <Box
                    key={color}
                    sx={{
                      backgroundColor: color,
                      width: '32px',
                      height: '32px',
                      borderRadius: '16px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      opacity: isColorUsed ? 0.5 : 1,
                      pointerEvents: isColorUsed ? 'none' : 'auto',
                      '&:hover': {
                        cursor: isColorUsed ? 'not-allowed' : 'pointer',
                      }
                    }}
                    onClick={() => !isColorUsed && setSelectedTagColor(color)}
                  >
                    {selectedTagColor === color && !isColorUsed && <img src={CheckedIcon} alt='checked' />}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: '10px' }}>
              <CustomButton
                variant='outlined'
                onClick={() => setOpenTagCreating(false)}
                customClass={styles.editBtn}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant='contained'
                onClick={handleAddTag}
                disabled={tagName.length === 0 || !selectedTagColor || tags?.some(tag => tag.name === tagName && (!isEditing || tag.id !== selectedTagId))}
                customClass={styles.editBtn}
              >
                {isEditing ? 'Edit tag' : 'Add tag'}
              </CustomButton>
            </Box>
            {tags?.some(tag => tag.name === tagName && (!isEditing || tag.id !== selectedTagId)) && <Typography variant='h10' sx={{ color: '#FF4D4F', marginTop: '10px' }}>
              Tag name already exists. Please choose another name.
            </Typography>}
          </Box>}
        </Box>
      </Box>
    </>
  )
}

export default Tags;
